import { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, KeyRound } from 'lucide-react';
import { formatApiError } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePasswordDialog({ open, onOpenChange, forced = false, onSuccess }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: current,
        new_password: next,
      });
      toast.success('Password updated');
      setCurrent(''); setNext(''); setConfirm('');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : onOpenChange}>
      <DialogContent className={`sm:max-w-md ${forced ? '[&>button:has(svg.lucide-x)]:hidden' : ''}`} onEscapeKeyDown={(e) => forced && e.preventDefault()} onPointerDownOutside={(e) => forced && e.preventDefault()} onInteractOutside={(e) => forced && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#1680e5]" />
            {forced ? 'Set your new password' : 'Change password'}
          </DialogTitle>
          <DialogDescription>
            {forced
              ? 'Your account is using a temporary password. Please set a new password to continue.'
              : 'Choose a new password (minimum 8 characters).'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current">{forced ? 'Temporary password' : 'Current password'}</Label>
            <Input
              id="current" type="password" required
              value={current} onChange={(e) => setCurrent(e.target.value)}
              data-testid="change-password-current"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="next">New password</Label>
            <Input
              id="next" type="password" required minLength={8}
              value={next} onChange={(e) => setNext(e.target.value)}
              data-testid="change-password-new"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm" type="password" required minLength={8}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="change-password-confirm"
            />
          </div>
          <Button
            type="submit" disabled={loading}
            className="w-full bg-[#1680e5] hover:bg-[#1680e5]/90"
            data-testid="change-password-submit"
          >
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating…</> : 'Update password'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
